import React, { useEffect } from 'react';
import { Prospect } from '../../shared/types';
import { Search, Bell, Plus, MessageSquare, Paperclip, MoreVertical } from 'lucide-react';
import { RealEstateSystem } from './RealEstateSystem';
import { HealthSystem } from './HealthSystem';
import { GastronomySystem } from './GastronomySystem';
import { LawSystem } from './LawSystem';
import { AutoSystem } from './AutoSystem';
import { ArchSystem } from './ArchSystem';
import { EcommerceSystem } from './EcommerceSystem';

interface PlataformaDemoProps {
  prospect: Prospect;
}

export const PlataformaDemo: React.FC<PlataformaDemoProps> = ({ prospect }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    if (prospect.theme) {
      const root = document.documentElement;
      root.style.setProperty('--ym-prospect-primary', prospect.theme.primary_color);
      if (prospect.theme.bg_color) root.style.setProperty('--ym-prospect-bg', prospect.theme.bg_color);
      if (prospect.theme.text_color) root.style.setProperty('--ym-prospect-text', prospect.theme.text_color);
      if (prospect.theme.surface_color) root.style.setProperty('--ym-prospect-surface', prospect.theme.surface_color);
    }
  }, [prospect]);

  if (prospect.availableDemos?.includes('imobiliaria')) {
    return <RealEstateSystem prospect={prospect} />;
  }
  
  if (prospect.availableDemos?.includes('clinica')) {
    return <HealthSystem prospect={prospect} />;
  }

  if (prospect.availableDemos?.includes('gastronomia')) {
    return <GastronomySystem prospect={prospect} />;
  }

  if (prospect.availableDemos?.includes('advocacia')) {
    return <LawSystem prospect={prospect} />;
  }

  if (prospect.availableDemos?.includes('automoveis')) {
    return <AutoSystem prospect={prospect} />;
  }

  if (prospect.availableDemos?.includes('arquitetura')) {
    return <ArchSystem prospect={prospect} />;
  }

  if (prospect.availableDemos?.includes('ecommerce')) {
    return <EcommerceSystem prospect={prospect} />;
  }

  const columns = [
    { title: 'Novos Contatos', count: 3, color: 'bg-blue-500' },
    { title: 'Triagem', count: 5, color: 'bg-amber-500' },
    { title: 'Cotação em Construção', count: 2, color: 'bg-purple-500' },
    { title: 'Proposta Enviada', count: 4, color: 'bg-emerald-500' },
  ];

  return (
    <div className="min-h-screen bg-[var(--ym-prospect-bg)] text-[var(--ym-prospect-text)] font-sans flex flex-col">
      {/* Top Navbar */}
      <header className="h-16 border-b border-[var(--ym-prospect-surface)]/20 px-6 flex items-center justify-between bg-white dark:bg-black/20">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded bg-[var(--ym-prospect-primary)] flex items-center justify-center text-white font-bold">
            {prospect.trade_name.charAt(0)}
          </div>
          <span className="font-bold">{prospect.trade_name} CRM</span>
        </div>
        
        <div className="flex items-center gap-6">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
            <input 
              type="text" 
              placeholder="Buscar clientes, orçamentos..." 
              className="pl-10 pr-4 py-2 bg-slate-100 dark:bg-slate-800 rounded-lg text-sm outline-none w-64"
            />
          </div>
          <Bell className="w-5 h-5 opacity-60" />
          <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700" />
        </div>
      </header>

      {/* Main Content: Kanban */}
      <main className="flex-1 overflow-x-auto p-6">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold mb-1 font-heading">Pipeline de Solicitações</h1>
            <p className="text-sm opacity-60">Gerencie orçamentos e atendimentos.</p>
          </div>
          <button className="bg-[var(--ym-prospect-primary)] text-white px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nova Solicitação
          </button>
        </div>

        <div className="flex gap-6 h-[calc(100vh-200px)]">
          {columns.map((col, idx) => (
            <div key={idx} className="flex-shrink-0 w-80 flex flex-col bg-slate-50 dark:bg-slate-900/50 rounded-xl p-4 border border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${col.color}`} />
                  <h3 className="font-semibold text-sm">{col.title}</h3>
                </div>
                <span className="text-xs font-medium bg-slate-200 dark:bg-slate-800 px-2 py-1 rounded-md">{col.count}</span>
              </div>

              {/* Fake Cards */}
              <div className="flex-1 overflow-y-auto space-y-3">
                {[1, 2].map((card) => (
                  <div key={card} className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm cursor-pointer hover:border-[var(--ym-prospect-primary)] transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-xs font-semibold px-2 py-1 bg-[var(--ym-prospect-primary)]/10 text-[var(--ym-prospect-primary)] rounded">
                        {idx === 0 ? 'Site' : 'WhatsApp'}
                      </span>
                      <MoreVertical className="w-4 h-4 opacity-40" />
                    </div>
                    <h4 className="font-bold text-sm mb-1">Família Silva</h4>
                    <p className="text-xs opacity-70 mb-4 line-clamp-1">Orçamento para Cancún (4 pessoas)</p>
                    
                    <div className="flex items-center justify-between text-xs opacity-60">
                      <div className="flex items-center gap-3">
                        <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> 2</span>
                        <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" /> 1</span>
                      </div>
                      <div className="w-6 h-6 rounded-full bg-slate-300 dark:bg-slate-600" title="Agente Responsável" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};
